import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { memo } from 'react';

import { ProductTypeBadge, SymbolMaxLeverageBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import { useResponsiveOverlay } from '@flipster/features/src/shared/responsive-overlay';
import { Link } from '@flipster/i18n/routing';
import { IconButton } from '@flipster/react/design-system/icon-button';
import Formatted from '@flipster/react/design-utils/formatted';
import { VirtualScroll } from '@flipster/react/design-utils/virtual-scroll';
import StarOutlineIcon from '@flipster/react/icon/generated/StarOutlineIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, linkOverlay } from '@flipster/styled-system/patterns';
import StarFilledIcon from '@flipster/ui/assets/svgs/filled/star-filled.svg';

import { EmptyPlaceholder } from './empty-placeholder';
import { marketListBunja, trackMarketListBunja } from './state';
import { colStyles } from './styles';

export const ListItems = () => {
  const { currentTabAtom, displayedSymbolsAtom, isLoadingAtom } = useBunja(marketListBunja);
  const currentTab = useAtomValue(currentTabAtom);
  const displayedSymbols = useAtomValue(displayedSymbolsAtom);
  const isLoading = useAtomValue(isLoadingAtom);

  return (
    <VirtualScroll
      items={displayedSymbols}
      itemHeight={58}
      totalItemCount={displayedSymbols.length}
      keyExtractor={(s) => s}
      renderItem={(symbol, index) => <MarketItem symbol={symbol} index={index} />}
      overscan={4}
      isLoading={isLoading}
      emptyPlaceholder={<EmptyPlaceholder />}
      key={currentTab} // scrollTop을 시키기 위한 trigger
    />
  );
};

const MarketItem = memo(({ symbol, index }: { symbol: string; index: number }) => {
  const { tickerAtomFamily, instrumentAtomFamily, favoritesAtom, toggleFavorite, currentTabAtom } =
    useBunja(marketListBunja);
  const instrument = useAtomValue(instrumentAtomFamily(symbol));
  const ticker = useAtomValue(tickerAtomFamily(symbol));
  const favorites = useAtomValue(favoritesAtom);
  const currentTab = useAtomValue(currentTabAtom);
  const { clickSymbol } = useBunja(trackMarketListBunja);
  const setOrderAttribution = useSetAtom(orderAttributionAtom);
  const { setOpen } = useResponsiveOverlay();

  const isFavoritesTab = currentTab === 'favorites';
  const isFavorite = favorites.has(symbol);
  const isPerpetual = instrument.productType === 'perpetual';

  return (
    <div
      className={hstack({
        pos: 'relative',
        textStyle: 'Text/M/Monospace/Regular',
        h: '100%',
        gap: 20,
        pr: 16,
        _hover: { bgColor: 'background.accent.secondary.tint' },
      })}
    >
      <Link
        href={`/trade/${instrument.productType}/${symbol}`}
        prefetch={false}
        className={linkOverlay()}
        onClick={() => {
          clickSymbol('Search list', symbol, index);
          setOrderAttribution(OrderAttributionMapper.trade[currentTab]);
          setOpen(false);
        }}
      />
      <div className={css(colStyles.symbol, hstack.raw())}>
        {isFavoritesTab && (
          <IconButton
            hierarchy='quaternary'
            icon={isFavorite ? <StarFilledIcon /> : <StarOutlineIcon />}
            onClick={async (e) => {
              e.stopPropagation();
              await toggleFavorite(symbol);
            }}
            css={{ zIndex: 1 }}
          />
        )}
        <SymbolDisplay
          symbol={symbol}
          css={{ flex: 1, minW: 0, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)' }}
          icon={<SymbolDisplay.Icon size={24} space={8} />}
          mainLabel={
            <div className={flex({ gap: 4, minW: 0, alignItems: 'center' })}>
              <SymbolDisplay.Label
                value='shortName'
                css={{
                  textStyle: 'Body/M/Strong',
                  color: 'content.default.level-5',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              />
              {isPerpetual && instrument.leverage != null ? (
                <SymbolMaxLeverageBadge
                  crossMax={instrument.leverage}
                  isolatedCap={instrument.maxInitLeverageIsolatedMargin}
                />
              ) : (
                !isPerpetual && <ProductTypeBadge productType={instrument.productType} />
              )}
            </div>
          }
          subLabel={
            instrument.isTradFi ? (
              <span
                className={css({
                  display: 'block',
                  textStyle: 'Caption/L/Monospace/Regular',
                  color: 'content.accent.promotion',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                })}
              >
                {instrument.displayName}
              </span>
            ) : undefined
          }
        />
      </div>
      <div className={css(colStyles.turnover24h, { pos: 'relative', zIndex: 1, color: 'content.default.level-5' })}>
        <Formatted.ShortNumber value={ticker?.turnover24h} precision={2} toFixed />
      </div>
      <div className={css(colStyles.priceChange, { pos: 'relative', zIndex: 1 })}>
        <Formatted.Percent
          value={ticker?.priceChangePct24h}
          precision={2}
          toFixed
          withColor
          withArrow
          css={{ textStyle: 'Body/M/Monospace/Strong' }}
        />
        <Formatted.Number
          value={ticker?.displayPrice}
          method='round-half-up'
          precision={instrument.pricePrecision}
          toFixed
          css={{ textStyle: 'Body/S/Monospace/Regular' }}
        />
      </div>
    </div>
  );
});
